import React from 'react';
import { Alert, LinearProgress, Stack, TextFieldProps } from '@mui/material';

import { useFetchEmployees } from 'utils/useFetchEmployees';
import { EmployeesQuery } from 'generated/graphql';

import { SearchInput } from './SearchInput';
import { UserList, UserListProps } from './UserList';
import { SelectedUserList } from './SelectedUserList';

export type Employee = EmployeesQuery['employees']['users'][number];

export type UserMultiSelectProps = {
  selectedUsers: Employee[];
  setSelectedUsers: (v: Employee[]) => void;
  label: TextFieldProps['label'];
  helperText?: TextFieldProps['helperText'];
  renderEmployee: UserListProps['renderEmployee'];
};

export const UserMultiSelect = ({
  selectedUsers,
  setSelectedUsers,
  label,
  helperText,
  renderEmployee,
}: UserMultiSelectProps) => {
  const { search, setSearch, result } = useFetchEmployees();
  const { data, loading, error, fetchMore } = result;
  const users = data?.employees?.users;
  const total = data?.employees?.total;

  return (
    <Stack spacing={2}>
      <SearchInput search={search} setSearch={setSearch} label={label} helperText={helperText} />

      {error && (
        <Alert severity="warning">
          An error occurred while searching for users. Please try again later.
        </Alert>
      )}

      {loading && <LinearProgress />}

      {search && users && (
        <UserList
          users={users}
          total={total || 0}
          loading={loading}
          fetchMore={(): void => {
            const skip = result?.data?.employees.users.length || 0;
            // @NOTE-AC: results get merged in app/entities/graphql.ts
            void fetchMore?.({ variables: { skip } });
          }}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          renderEmployee={renderEmployee}
        />
      )}

      {selectedUsers.length > 0 && (
        <SelectedUserList
          users={selectedUsers}
          removeUser={(userId): void =>
            setSelectedUsers(selectedUsers.filter((user) => user.id !== userId))
          }
        />
      )}
    </Stack>
  );
};
